import _ from 'lodash';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { DMSDeal } from '@upstart/harmony/lib/legos/dmsTypes';
import { isResponseEditable } from '@upstart/harmony/lib/response-utils';
import { useState } from 'react';
import { useAsyncCallback, UseAsyncReturn } from 'react-async-hook';
import { FinanceResponse } from '@upstart/harmony/lib/legos/financingTypes';
import FinanceResponseService from '../../services/FinanceResponseService';
import DMSService from '../../services/DMSService';
import { parseDMSError } from '../../services/DMSService/errorHandling';
import { buildFinanceResponseWithDMSDeal } from './utils';
import {
  isEmbeddedInClosingWorkflow,
  sendClosingWorkflowPullFromDmsResultToCC,
  sendRicFormUpdateToCC,
} from '../../util/commandCenter';

type UseGetFinanceResponseForRIC = {
  isDMSFlowEnabled: boolean;
  showCDKSaveDealNotice: boolean;
  asyncResponse: UseAsyncReturn<FinanceResponse>;
  dmsPullError?: string | null;
};

const DMS_DEAL_INFO_PATH = 'financeMeta.dmsDealInfo';
const UAR_DEAL_UNDEFINED_ERROR = 'Missing Deal Information';
const DEFAULT_DMS_ERROR_MSG = 'Error pulling from DMS. Please try again.';

/**
 * Fetches Finance Response for use in the RIC Review page
 */
export const useGetFinanceResponseForRIC = ({
  responseId,
  deal,
  dmsType,
}: {
  responseId: string;
  deal?: Deal;
  dmsType?: string | null;
}): UseGetFinanceResponseForRIC => {
  const isDMSFlowEnabled = _.has(deal, DMS_DEAL_INFO_PATH);
  const [dmsPullError, setDmsPullError] = useState<string | undefined | null>();
  // whether to display warning modal before RIC submission
  const [showCDKSaveDealNotice, setShowCDKSaveDealNotice] = useState<boolean>(
    false,
  );

  const pullFromDMS = async (dmsDealId: string): Promise<DMSDeal | null> => {
    if (!deal) {
      setDmsPullError(UAR_DEAL_UNDEFINED_ERROR);
      return null;
    }
    try {
      const dmsDeal = await DMSService.pullFromDMS(deal, dmsDealId);
      setDmsPullError(null);
      return dmsDeal;
    } catch (error) {
      const errorMessage = parseDMSError(error, DEFAULT_DMS_ERROR_MSG, {
        dmsDealId,
        dmsType,
      });
      sendClosingWorkflowPullFromDmsResultToCC({
        success: false,
        error: errorMessage,
      });
      if (!isEmbeddedInClosingWorkflow()) {
        // closing workflow RIC review has separate error UI if pull fails so we only set this if NOT embedded
        setDmsPullError(errorMessage);
      }
      return null;
    }
  };

  const asyncResponse = useAsyncCallback<FinanceResponse>(
    async (forceDMSPull?: boolean) => {
      let financeResponse = await FinanceResponseService.get(responseId, {
        withRICDefaults: true,
      });

      sendRicFormUpdateToCC({
        ricFormValues: financeResponse,
      });

      if (
        isDMSFlowEnabled &&
        (forceDMSPull || isResponseEditable(financeResponse))
      ) {
        const dmsDealInfo = _.get(deal, 'financeMeta.dmsDealInfo', undefined);
        const latestDMSDeal = await pullFromDMS(dmsDealInfo?.id);
        if (latestDMSDeal) {
          sendClosingWorkflowPullFromDmsResultToCC({
            success: true,
            dmsDealId: latestDMSDeal.id,
          });
          financeResponse = buildFinanceResponseWithDMSDeal(
            financeResponse,
            latestDMSDeal,
          );
          // if checksum is same it means the deal was not saved in CDK
          if (dmsDealInfo && dmsDealInfo.checksum === latestDMSDeal.checksum) {
            setShowCDKSaveDealNotice(true);
          }
        }
      }
      return financeResponse;
    },
  );

  return {
    isDMSFlowEnabled,
    showCDKSaveDealNotice,
    asyncResponse,
    dmsPullError,
  };
};
